import {CommonModule} from '@angular/common';
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {Recipient} from '@gatewee/types/recipient';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable, Subject, takeUntil} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeMultiSelectComponent} from '../../gatewee-multi-select/gatewee-multi-select.component';

interface RecipientsServiceInterface {
  findAll(): Observable<Recipient[]>;
}

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
  recipientsService: RecipientsServiceInterface;
}

@Component({
  selector: 'gatewee-web-split-whitelist',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeMultiSelectComponent,
  ],
  templateUrl: './split-whitelist.component.html',
})
export class SplitWhitelistComponent implements OnInit, OnDestroy {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
  recipientsService!: RecipientsServiceInterface;

  private readonly destroy$ = new Subject<void>();

  recipients: Recipient[] = [];
  items: {label: string; value: string}[] = [];
  loadingRecipients = false;

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    const data = this.context.data;
    this.id = data.id;
    this.setting = data.setting;
    this.editSettingFn = data.editSettingFn;
    this.recipientsService = data.recipientsService;
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadRecipients();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get control(): FormControl<string[]> {
    return this.formGroup.get('whitelist') as FormControl<string[]>;
  }

  async onSubmit(): Promise<void> {
    if (this.formGroup.invalid || this.submitting) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.submitting = true;

    const selected = this.control.value ?? [];
    const serializedValue = this.serializeSelected(selected);
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: serializedValue,
    };

    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: Error) => {
        console.error('Error updating setting:', error);
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }

  private loadRecipients(): void {
    if (!this.recipientsService) {
      console.warn('RecipientsService not provided. Cannot load recipients.');
      return;
    }

    this.loadingRecipients = true;
    this.recipientsService
      .findAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (recipients: Recipient[]) => {
          this.recipients = recipients;
          this.items = recipients.map((recipient: Recipient) => ({
            label: `ID: ${recipient.id} - ${recipient.name}`,
            value: recipient.id.toString(),
          }));
          this.refreshWhitelistControlAfterItemsLoaded();
          this.loadingRecipients = false;
        },
        error: () => {
          this.loadingRecipients = false;
        },
      });
  }

  private initializeForm(): void {
    const currentValue = this.parseExistingValue();
    this.formGroup = this.fb.group({
      whitelist: [currentValue],
    });
  }

  /** Re-sync chips when options load so saved IDs resolve to labels. */
  private refreshWhitelistControlAfterItemsLoaded(): void {
    if (!this.formGroup) {
      return;
    }
    const current = this.control.value ?? [];
    this.control.setValue([...current], {emitEvent: false});
  }

  private parseExistingValue(): string[] {
    const value = this.setting?.value;

    if (Array.isArray(value)) {
      return value.map(item => String(item));
    }

    if (typeof value === 'string' && value.trim() !== '') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map(item => String(item));
        }
      } catch {
        return value
          .split(',')
          .map(item => item.trim())
          .filter(Boolean);
      }
    }

    return [];
  }

  private serializeSelected(values: string[]): string {
    const normalized = values
      .map(item => item?.trim())
      .filter((item): item is string => !!item);
    const numericList = normalized
      .map(item => Number(item))
      .filter(item => !Number.isNaN(item));

    const shouldUseNumeric = numericList.length === normalized.length;
    return JSON.stringify(shouldUseNumeric ? numericList : normalized);
  }
}
